import { CurrencyPipe, DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { SendOption } from "@models/annual-statements/annual-statement-enums";
import { Contact } from "@models/common/contact";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { AdHocDebtReminderModel } from "@models/reminders/ad-hoc-debt-reminder-model";
import { AdHocPaymentReminderModel } from "@models/reminders/ad-hoc-payment-reminder-model";
import { AdHocReminderModel } from "@models/reminders/ad-hoc-reminder-model";
import { ReminderRecord } from '@models/reminders/reminder-record';
import { ReminderSendingType } from "@models/reminders/reminder.enum";
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import {
  ShadowDomViewerComponent
} from "@shared/components/email-preview/shadow-dom-viewer/shadow-dom-viewer.component";
import { somethingWentWrong } from "@shared/constants/common-error-messages";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import { AppCopyDirective } from "@shared/directives/copy.directive";
import { BillPayCodeFormatPipe } from "@shared/pipes/billpay-code-format.pipe";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { catchError, map, Observable, of } from "rxjs";
import { ContactsSelectComponent } from "../../../../../forms/controls/contacts-select/contacts-select.component";
import { BaseAdHocReminderComponent } from "../base-ad-hoc-reminder/base-ad-hoc-reminder.component";

@Component({
  selector: 'app-ad-hoc-payment-reminder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgxSkeletonLoaderModule,
    CurrencyPipe,
    DatePipe,
    RadioComponent,
    DividerComponent,
    YesNoControlComponent,
    ContactsSelectComponent,
    CommonModalFormComponent,
    ShadowDomViewerComponent,
    BillPayCodeFormatPipe,
    RouterLink,
    AppCopyDirective
  ],
  providers: [EmailsApiService, TextMessageApiService],
  templateUrl: 'ad-hoc-payment-reminder.component.html',
  styleUrl: 'ad-hoc-payment-reminder.component.scss'
})
export class AdHocPaymentReminderComponent extends BaseAdHocReminderComponent implements OnInit {
  individualApiService = inject(IndividualsApiService);

  @Input() isSendDebtReminder = false;

  @Output() sendPaymentAdHocReminder = new EventEmitter<ReminderRecord>();
  @Output() sendDebtAdHocReminder = new EventEmitter<ReminderRecord>();

  currentDate = DatepickerHelper.getToday();
  isPaymentUrlLoading = false;
  paymentUrl: string | null = '';

  override ngOnInit(): void {
    super.ngOnInit();
    this.company = this.documentBase?.company ?? this.company;
    this.getPaymentUrl();
  }

  override getContacts(): Observable<Contact[]> {
    if (!this.company) return of([]);
    const individualsIds = this.company.currentOfficers
      .filter((officer) => officer.type == RelationshipType.Director)
      .map((director) => director.individualId)
      .filter(Boolean) as string[];

    return this.individualApiService.getIndividualListWithContacts(individualsIds)
      .pipe(
        map((individuals) => {
          const companyDirectorsContacts: Contact[] = individuals.flatMap((individual) => individual.contacts);
          const contactsMap = new Map<string, Contact>();

          companyDirectorsContacts.forEach((contact) => {
            if (contact?.contactId) {
              contactsMap.set(contact?.contactId, contact);
            }
          });

          return [...contactsMap.values()];
        }),
        catchError((error) => {
          console.warn('[getIndividualListContacts] error: ', error);
          this.toastr.error('Error happened while loading contacts', 'Error');
          return of([this.company?.billingContact, this.company?.signingContact].filter(Boolean) as Contact[]);
        })
      );
  }

  override onModalDataLoaded(): void {
    const contacts = this.contacts.slice();
    let recipientContact: Contact | null = null;
    const firstDirectorContact = contacts[0];

    if (this.company?.billingContact) {
      // Select Billing Contact by default
      recipientContact = this.company.billingContact;

      const contactsIncludeBillingContact = contacts.find((contact) => contact.contactId == this.company?.billingContactId);

      if (!contactsIncludeBillingContact) {
        contacts.unshift(this.company.billingContact);
      }
    }

    if (this.company?.signingContact) {
      if (!recipientContact) {
        // Select Signing Contact by default if Billing Contact is empty
        recipientContact = this.company.signingContact;
      }

      const contactsIncludeSigningContact = contacts.find((contact) => contact.contactId == this.company?.billingContactId);

      if (!contactsIncludeSigningContact) {
        contacts.unshift(this.company.signingContact);
      }
    }

    if (!recipientContact && contacts.length && firstDirectorContact) {
      // Select First Director Contact by default
      // if Billing Contact and Signing Contacts are empty
      recipientContact = firstDirectorContact ?? null;
    }

    this.contacts = contacts;
    this.form.patchValue({
      recipientPhones: recipientContact?.contactId ?? null,
      recipientEmails: recipientContact?.contactId ?? null,
    });

    this.onRecipientEmailChanged(recipientContact);
    this.onRecipientPhoneChanged(recipientContact);
    super.onModalDataLoaded();
  }

  getPaymentUrl(): void {
    if (!this.company?.postBillpay) return;

    this.isPaymentUrlLoading = true;
    this.configSettingsService.getPaymentUrl()
      .subscribe({
        next: (paymentUrl) => {
          this.paymentUrl = paymentUrl ?? null;
          this.isPaymentUrlLoading = false;
        },
        error: (error) => {
          this.toastr.error(somethingWentWrong, 'Error');
          console.warn('[getPaymentUrl] error: ', error);
        }
      });
  }

  // Submitting
  sendReminder(): void {
    const sendingType = this.form.controls.sendBy.value === SendOption.Email ? ReminderSendingType.Email : ReminderSendingType.SMS;
    const addHocReminderModel = new AdHocReminderModel({
      sendingType,
      documentId: this.documentBase?.documentSigning?.documentId ?? null,
      entityId: this.company?.entityId,
      emailPayloadModel: this.emailPayloadModel,
      emailTemplate: this.buildEmailTemplate(),
      textPayloadModel: this.smsPayloadModel,
      textTemplate: this.smsTemplate,
    });

    if (!this.isSelectedContactValid()) {
      this.toastr.error('Please fill required fields', 'Error');
      this.form.markAllAsTouched();
      return;
    }

    if (this.isAnnualReminder) {
      const payload = new AdHocPaymentReminderModel({
        ...addHocReminderModel,
        includeInvoice: sendingType === ReminderSendingType.Email && (this.form.controls.attachInvoice.value ?? false),
        companyName: this.company?.name ?? '',
      });

      if (this.form.controls.sendBy.value == SendOption.Email) {
        const email = this.contacts.find((contact) => contact.contactId == this.form.controls.recipientEmails.value)?.email;
        payload.emails = [email] as string[];
      } else if (this.form.controls.sendBy.value == SendOption.Sms) {
        payload.phones = [this.selectedRecipientPhonesContact!.phone];
      }

      this.isLoading = true;
      setControlDisabled(this.form);

      this.remindersService.sendPaymentAdHocReminder(payload)
        .subscribe({
          next: (reminder) => {
            this.toastr.success("The payment reminder has been successfully sent to the recipients", "Success");
            this.sendPaymentAdHocReminder.emit(reminder);
            this.activeModal.close();
          },
          error: (err: HttpErrorResponse) => {
            if (typeof err.error === 'string' && err.error.includes('System.NullReferenceException')) {
              this.toastr.error("The invoice doesn't exist. The payment reminder has not been sent.", 'Error');
            } else {
              this.toastr.error("The payment reminder hasn't been sent", 'Error');
            }

            this.onSubmitRequestError();
          }
        });
    } else {
      const payload = new AdHocDebtReminderModel({
        ...addHocReminderModel,
        attachPayslip: sendingType === ReminderSendingType.Email && (this.form.controls.attachInvoice.value ?? false),
        companyName: this.company?.name ?? '',
        phones: this.form.controls.recipientPhones.value ? [this.contacts.find((contact) => contact.contactId == this.form.controls.recipientPhones.value)?.phone ?? ''] : [],
      });

      if (this.form.controls.sendBy.value == SendOption.Email) {
        const email = this.contacts.find((contact) => contact.contactId == this.form.controls.recipientEmails.value)?.email;
        payload.emails = [email] as string[];
      }

      this.isLoading = true;
      setControlDisabled(this.form);

      this.remindersService.sendDebtAdHocReminder(payload)
        .subscribe({
          next: (reminder) => {
            this.toastr.success("The debt reminder has been successfully sent to the recipients", "Success");
            this.sendDebtAdHocReminder.emit(reminder);
            this.activeModal.close();
          },
          error: (err: HttpErrorResponse) => {
            if (typeof err.error === 'string' && err.error.includes('System.NullReferenceException')) {
              this.toastr.error("The invoice doesn't exist. The debt reminder has not been sent.", 'Error');
            } else {
              this.toastr.error("The debt reminder hasn't been sent", 'Error');
            }

            this.onSubmitRequestError();
          }
        });
    }
  }

  refreshDebt(): void {
    const companyId = this.company?.entityId ?? '';
    if (!companyId) return;

    this.annualStatementsApiService.annualRegenerate(companyId).subscribe();
  }
}
